import { beHttp } from '../beHttp';
import { secureApiPost } from '../secureHttp';

export interface PlatformPreferences {
  first_time: boolean;
  nav_onboarding: boolean;
  sessions_onboarding: boolean;
  schedule_onboarding: boolean;
  profile_onboarding: boolean;
  patients_onboarding: boolean;
  specific_day_onboarding: boolean;
  block_schedule_onboarding: boolean;
  reschedule_slots_onboarding: boolean;
  /**
   * true = el psicólogo ya vio el modal de felicitación por su primera sesión completada.
   * Persiste en DB para sobrevivir cambios de dispositivo.
   * Requiere columna `first_session_welcome_seen Boolean @default(false)` en
   * `psychologist_platform_preferences` (migración backend pendiente).
   */
  first_session_welcome_seen: boolean;
  acuity_schedules_pending_review: boolean;
  acuity_migration_in_progress: boolean;
  is_acuity: boolean;
  /**
   * Plataforma de origen de la psicóloga (columna `origin_platform` en
   * `psychologist_platform_preferences`). `'acuity'` indica que viene migrada
   * desde Acuity. Default backend: `'psi-platform'`.
   */
  origin_platform: string;
}

export async function getPlatformPreferences(): Promise<PlatformPreferences> {
  return await beHttp.get<PlatformPreferences>(
    '/api/psychologist/platform-preferences',
    undefined,
    { showErrorToast: false },
  );
}

/**
 * Claves que el PATCH acepta. `first_time`, `acuity_migration_in_progress`, `is_acuity`
 * y `origin_platform` son de solo lectura: el schema del backend es `.strict()` y
 * responde 400 `unrecognized_keys` si se envían. Tipar el update con este subconjunto
 * hace que el error lo detecte el compilador en vez del runtime.
 */
export type UpdatablePlatformPreferences = Pick<
  PlatformPreferences,
  | 'nav_onboarding'
  | 'sessions_onboarding'
  | 'schedule_onboarding'
  | 'profile_onboarding'
  | 'patients_onboarding'
  | 'specific_day_onboarding'
  | 'block_schedule_onboarding'
  | 'reschedule_slots_onboarding'
  | 'first_session_welcome_seen'
  | 'acuity_schedules_pending_review'
>;

export async function updatePlatformPreferences(
  data: Partial<UpdatablePlatformPreferences>,
): Promise<PlatformPreferences> {
  return await beHttp.patch<PlatformPreferences>(
    '/api/psychologist/platform-preferences',
    data,
    { showSuccessToast: false, showErrorToast: false },
  );
}

/**
 * Dispara la migración Acuity → psi-platform para la psicóloga logueada.
 * El backend responde 202 inmediato y corre el trabajo en background.
 * Hay que pollear `acuity_migration_in_progress` para saber cuándo termina.
 */
export async function migrateFromAcuity(): Promise<{ accepted: true }> {
  return await secureApiPost<{ accepted: true }>(
    '/v1/psi-platform/psychologist/migrate-from-acuity',
    undefined,
    { showSuccessToast: false, showErrorToast: false },
  );
}
